import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button,
  Grid, Alert, Divider, MenuItem, CircularProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../../services/api.js';
import AudioRecorder from '../../components/AudioRecorder.jsx';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';

const EMPTY_FORM = {
  code:           '',
  name:           '',
  city:           '',
  headFirstName:  '',
  headLastName:   '',
  secondHead:     '',
  schedule:       '',
  subject:        '',
  simanim:        '',
  additions:      '',
  gabaiName:      '',
  gabaiPhone:     '',
  totalQuestions: '',
  memberCount:    '',
  audioFilename:  '',
  status:         'active',
};

const SCHEDULE_OPTIONS = ['לפנה"צ', 'אחה"צ', 'ערב', 'כל היום', ''];

const KolelCard = ({ recordId, onClose }) => {
  const params   = useParams();
  const navigate = useNavigate();
  const id  = recordId || params.id;
  const isNew = id === 'new';

  const [form, setForm]             = useState(EMPTY_FORM);
  const [loading, setLoading]       = useState(!isNew);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [audioSrc, setAudioSrc]     = useState(null);
  const [audioUploading, setAudioUploading] = useState(false);

  // טעינת קובץ השמע כ-blob URL (משמר על אותנטיקציה)
  useEffect(() => {
    let objectUrl = null;
    if (!isNew && form.audioFilename) {
      api.get(`/kolels/${id}/audio`, { responseType: 'blob' })
        .then(({ data }) => {
          objectUrl = URL.createObjectURL(data);
          setAudioSrc(objectUrl);
        })
        .catch(() => setAudioSrc(null));
    } else {
      setAudioSrc(null);
    }
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id, form.audioFilename]);

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('audio', file);
    try {
      setAudioUploading(true);
      setError('');
      const { data } = await api.post(`/kolels/${id}/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, audioFilename: data.audioFilename }));
      setSuccess(data.yemotSynced
        ? 'קובץ השמע הועלה בהצלחה וסונכרן לקו הטלפון'
        : 'קובץ השמע הועלה בהצלחה (לא סונכרן לקו — בדוק הגדרת טוקן ימות)');
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בהעלאת קובץ שמע');
    } finally {
      setAudioUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveAudio = async () => {
    try {
      setError('');
      await api.delete(`/kolels/${id}/audio`);
      setForm((f) => ({ ...f, audioFilename: '' }));
      setSuccess('קובץ השמע הוסר');
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בהסרת קובץ שמע');
    }
  };

  // שמירת הקלטה שנוצרה בדפדפן כקובץ שמע של הכולל
  const handleRecordedAudio = async (blob) => {
    const ext = blob.type.includes('ogg') ? 'ogg' : 'webm';
    const file = new File([blob], `recording.${ext}`, { type: blob.type });
    const formData = new FormData();
    formData.append('audio', file);
    try {
      setAudioUploading(true);
      setError('');
      const { data } = await api.post(`/kolels/${id}/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, audioFilename: data.audioFilename }));
      setSuccess(data.yemotSynced
        ? 'ההקלטה נשמרה בהצלחה וסונכרנה לקו הטלפון'
        : 'ההקלטה נשמרה בהצלחה (לא סונכרנה לקו — בדוק הגדרת טוקן ימות)');
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירת ההקלטה');
    } finally {
      setAudioUploading(false);
    }
  };

  useEffect(() => {
    if (!isNew) {
      setLoading(true);
      api.get(`/kolels/${id}`)
        .then(({ data }) => setForm(data))
        .catch(() => setError('שגיאה בטעינת נתוני כולל'))
        .finally(() => setLoading(false));
    }
  }, [id]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    // המרת קוד למספר אם הוזן
    const payload = {
      ...form,
      code: form.code !== '' && form.code !== null ? Number(form.code) : null,
    };
    try {
      if (isNew) {
        const { data } = await api.post('/kolels', payload);
        setSuccess('כולל נוצר בהצלחה');
        if (onClose) onClose('created');
        else navigate(`/kolels/${data._id}`);
      } else {
        const { data } = await api.put(`/kolels/${id}`, payload);
        setForm(data);
        setSuccess('נשמר בהצלחה');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאת שמירה');
    }
  };

  if (loading) return <Box><Typography>טוען...</Typography></Box>;

  return (
    <Box>
      <AdminPageHeader
        title={isNew ? 'כולל חדש' : form.name || 'כולל'}
        sub={isNew ? 'כרטיס כולל' : [form.code ? `קוד ${form.code}` : null, form.city].filter(Boolean).join(' · ') || 'כרטיס כולל'}
        back={!recordId ? { label: 'כוללים', onClick: () => navigate('/kolels') } : undefined}
        legacy={(
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {isNew ? 'כולל חדש' : form.name || 'כולל'}
          </Typography>
        )}
      />

      {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3 }}>

        {/* פרטי כולל */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>פרטי כולל</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <TextField
              label="קוד"
              fullWidth size="small" type="number"
              value={form.code ?? ''}
              onChange={handleChange('code')}
              helperText="קוד מספרי ייחודי"
            />
          </Grid>
          <Grid item xs={6} sm={5}>
            <TextField
              label="שם הכולל"
              fullWidth size="small"
              value={form.name || ''}
              onChange={handleChange('name')}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="עיר"
              fullWidth size="small"
              value={form.city || ''}
              onChange={handleChange('city')}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              select label="סדר לימוד"
              fullWidth size="small"
              value={form.schedule || ''}
              onChange={handleChange('schedule')}
            >
              {SCHEDULE_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s || '—'}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="מקצוע"
              fullWidth size="small"
              value={form.subject || ''}
              onChange={handleChange('subject')}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              select label="סטטוס"
              fullWidth size="small"
              value={form.status || 'active'}
              onChange={handleChange('status')}
            >
              <MenuItem value="active">פעיל</MenuItem>
              <MenuItem value="inactive">לא פעיל</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* ראש כולל */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>ראש כולל</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <TextField
              label="שם פרטי (ראש כולל)"
              fullWidth size="small"
              value={form.headFirstName || ''}
              onChange={handleChange('headFirstName')}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="שם משפחה (ראש כולל)"
              fullWidth size="small"
              value={form.headLastName || ''}
              onChange={handleChange('headLastName')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="ראש כולל שני"
              fullWidth size="small"
              value={form.secondHead || ''}
              onChange={handleChange('secondHead')}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* גבאי */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>גבאי</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={6}>
            <TextField
              label="שם גבאי"
              fullWidth size="small"
              value={form.gabaiName || ''}
              onChange={handleChange('gabaiName')}
            />
          </Grid>
          <Grid item xs={6} sm={6}>
            <TextField
              label="טלפון גבאי"
              fullWidth size="small"
              value={form.gabaiPhone || ''}
              onChange={handleChange('gabaiPhone')}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* נתונים נוספים */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>נתונים נוספים</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <TextField
              label="מספר אברכים"
              fullWidth size="small"
              value={form.memberCount || ''}
              onChange={handleChange('memberCount')}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label='סה"כ שאלות'
              fullWidth size="small"
              value={form.totalQuestions || ''}
              onChange={handleChange('totalQuestions')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="סימנים"
              fullWidth size="small"
              value={form.simanim || ''}
              onChange={handleChange('simanim')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="הוספות ותיקונים"
              fullWidth size="small" multiline rows={2}
              value={form.additions || ''}
              onChange={handleChange('additions')}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* IVR */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          IVR — ימות המשיח
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              קובץ שמע להכרזת שם הכולל בתפריט IVR.
              {' '}אם לא הועלה קובץ, המערכת תקרא את שם הכולל באופן אוטומטי.
            </Typography>

            {isNew ? (
              <Typography variant="caption" color="text.secondary">
                ניתן להעלות קובץ שמע לאחר יצירת הכולל.
              </Typography>
            ) : form.audioFilename ? (
              <Box sx={{ mt: 1 }}>
                {audioSrc && (
                  <audio
                    controls
                    src={audioSrc}
                    style={{ width: '100%', maxWidth: 380, display: 'block', marginBottom: 8 }}
                  />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <MusicNoteIcon fontSize="small" color="primary" />
                  <Typography variant="caption" color="text.secondary">
                    {form.audioFilename}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    component="label"
                    size="small"
                    variant="outlined"
                    startIcon={audioUploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
                    disabled={audioUploading}
                  >
                    {audioUploading ? 'מעלה...' : 'החלף קובץ'}
                    <input type="file" accept="audio/*" hidden onChange={handleAudioUpload} />
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={handleRemoveAudio}
                    disabled={audioUploading}
                  >
                    הסר קובץ
                  </Button>
                </Box>
                <AudioRecorder
                  onSave={handleRecordedAudio}
                  saving={audioUploading}
                  disabled={audioUploading}
                />
              </Box>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={audioUploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
                  disabled={audioUploading}
                >
                  {audioUploading ? 'מעלה...' : 'העלה קובץ שמע'}
                  <input type="file" accept="audio/*" hidden onChange={handleAudioUpload} />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  פורמטים נתמכים: mp3, wav, ogg, m4a — מקסימום 20MB
                </Typography>
                <AudioRecorder
                  onSave={handleRecordedAudio}
                  saving={audioUploading}
                  disabled={audioUploading}
                />
              </Box>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={handleSave}>
            {isNew ? 'צור כולל' : 'שמור שינויים'}
          </Button>
          <Button variant="outlined" onClick={() => onClose ? onClose() : navigate('/kolels')}>
            חזרה לרשימה
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default KolelCard;
